import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  HelpCircle, Plus, Search, Filter, Clock, CheckCircle2, 
  AlertCircle, MessageSquare, Paperclip, Send, X, Upload,
  ChevronRight, ArrowLeft, Loader2, Download, Image as ImageIcon,
  FileText, File
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';

const STATUS_CONFIG = {
  open: { label: 'Abierto', color: 'bg-blue-500/20 text-blue-400', icon: AlertCircle },
  in_progress: { label: 'En proceso', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  waiting: { label: 'Esperando', color: 'bg-purple-500/20 text-purple-400', icon: Clock },
  resolved: { label: 'Resuelto', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  closed: { label: 'Cerrado', color: 'bg-gray-500/20 text-gray-400', icon: CheckCircle2 }
};

const CATEGORY_CONFIG = {
  general: { label: 'General', emoji: '💬' },
  technical: { label: 'Técnico', emoji: '🔧' },
  billing: { label: 'Facturación', emoji: '💳' },
  feature: { label: 'Sugerencia', emoji: '💡' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Baja', color: 'text-gray-400' },
  normal: { label: 'Normal', color: 'text-blue-400' },
  high: { label: 'Alta', color: 'text-amber-400' },
  urgent: { label: 'Urgente', color: 'text-red-400' }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const UserSupport = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // New ticket
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicketForm, setNewTicketForm] = useState({
    subject: '',
    message: '',
    category: 'general',
    priority: 'normal'
  });
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Reply
  const [replyMessage, setReplyMessage] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await api.get(`/support/tickets${params}`);
      setTickets(res.data.tickets || []);
    } catch (err) {
      toast.error('Error cargando tickets');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  const fetchTicketDetail = async (ticketId) => {
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setSelectedTicket(res.data);
    } catch (err) {
      toast.error('Error cargando ticket');
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleFileSelect = (e, isReply = false) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024); // 10MB limit
    
    if (validFiles.length < files.length) {
      toast.error('Algunos archivos exceden el límite de 10MB');
    }
    
    if (isReply) {
      setReplyAttachments(prev => [...prev, ...validFiles]);
    } else {
      setAttachments(prev => [...prev, ...validFiles]);
    }
    e.target.value = '';
  };

  const removeAttachment = (index, isReply = false) => {
    if (isReply) {
      setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    } else {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicketForm.subject.trim() || !newTicketForm.message.trim()) {
      toast.error('Completa el asunto y mensaje');
      return;
    }
    
    setSubmitting(true);
    try {
      // Create ticket
      const res = await api.post('/support/tickets', newTicketForm);
      const ticketId = res.data.ticket.id;
      
      // Upload attachments
      for (const file of attachments) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/support/tickets/${ticketId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Ticket creado correctamente');
      setShowNewTicket(false);
      setNewTicketForm({ subject: '', message: '', category: 'general', priority: 'normal' });
      setAttachments([]);
      fetchTickets();
    } catch (err) {
      toast.error('Error creando ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Escribe un mensaje');
      return;
    }
    
    setSendingReply(true);
    try {
      // Send reply
      const res = await api.post(`/support/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage
      });
      
      const messageId = res.data.message.id;
      
      // Upload attachments
      for (const file of replyAttachments) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('message_id', messageId);
        await api.post(`/support/tickets/${selectedTicket.id}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Respuesta enviada');
      setReplyMessage('');
      setReplyAttachments([]);
      fetchTicketDetail(selectedTicket.id);
    } catch (err) {
      toast.error('Error enviando respuesta');
    } finally {
      setSendingReply(false);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return ImageIcon;
    if (contentType?.includes('pdf')) return FileText;
    return File;
  };

  // Ticket Detail View
  if (selectedTicket) {
    const StatusIcon = STATUS_CONFIG[selectedTicket.status]?.icon || AlertCircle;
    
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSelectedTicket(null)}
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Volver
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-text-muted text-sm">#{selectedTicket.id}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[selectedTicket.status]?.color}`}>
                  {STATUS_CONFIG[selectedTicket.status]?.label}
                </span>
              </div>
              <h1 className="text-xl font-bold text-text-main">{selectedTicket.subject}</h1>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden mb-4">
            <div className="divide-y divide-border-subtle">
              {selectedTicket.messages?.map((msg, idx) => (
                <div 
                  key={msg.id} 
                  className={`p-4 ${msg.sender === 'admin' ? 'bg-brand/5' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      msg.sender === 'admin' ? 'bg-brand text-white' : 'bg-surface-hover text-text-main'
                    }`}>
                      {msg.sender === 'admin' ? '👨‍💼' : msg.sender_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-text-main">
                          {msg.sender === 'admin' ? 'Soporte FASCINAI' : msg.sender_name}
                        </span>
                        <span className="text-xs text-text-muted">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-text-main whitespace-pre-wrap">{msg.message}</p>
                      
                      {/* Attachments */}
                      {msg.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {msg.attachments.map(att => {
                            const Icon = getFileIcon(att.content_type);
                            return (
                              <a
                                key={att.id}
                                href={`/api/support/tickets/${selectedTicket.id}/attachments/${att.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-1.5 bg-app-bg rounded-lg text-sm text-text-muted hover:text-text-main transition-colors"
                              >
                                <Icon className="w-4 h-4" />
                                <span className="truncate max-w-[150px]">{att.filename}</span>
                                <Download className="w-3 h-3" />
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reply Box */}
          {selectedTicket.status !== 'closed' && (
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <Textarea
                placeholder="Escribe tu respuesta..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                className="min-h-[100px] mb-3 bg-app-bg border-border-subtle"
              />
              
              {/* Reply Attachments */}
              {replyAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {replyAttachments.map((file, idx) => (
                    <div key={file.name || `reply-attachment-${idx}`} className="flex items-center gap-2 px-3 py-1.5 bg-app-bg rounded-lg text-sm">
                      <Paperclip className="w-4 h-4 text-text-muted" />
                      <span className="truncate max-w-[150px]">{file.name}</span>
                      <button onClick={() => removeAttachment(idx, true)} className="text-red-400 hover:text-red-300">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div>
                  <input
                    type="file"
                    ref={replyFileInputRef}
                    onChange={(e) => handleFileSelect(e, true)}
                    className="hidden"
                    multiple
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => replyFileInputRef.current?.click()}
                  >
                    <Paperclip className="w-4 h-4 mr-1" /> Adjuntar
                  </Button>
                </div>
                <Button 
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyMessage.trim()}
                  className="bg-brand"
                >
                  {sendingReply ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-1" />
                  )}
                  Enviar
                </Button>
              </div>
            </div>
          )}
        </div>
      </MainLayout>
    );
  }

  // Tickets List View
  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-main flex items-center gap-2">
              <HelpCircle className="w-7 h-7 text-brand" />
              Centro de Soporte
            </h1>
            <p className="text-text-muted mt-1">¿Necesitas ayuda? Abre un ticket y te responderemos pronto.</p>
          </div>
          <Button onClick={() => setShowNewTicket(true)} className="bg-brand">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Ticket
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {['all', 'open', 'in_progress', 'resolved', 'closed'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                statusFilter === status
                  ? 'bg-brand text-white'
                  : 'bg-surface text-text-muted hover:text-text-main'
              }`}
            >
              {status === 'all' ? 'Todos' : STATUS_CONFIG[status]?.label}
            </button>
          ))}
        </div>

        {/* Tickets List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-xl border border-border-subtle">
              <MessageSquare className="w-12 h-12 text-text-muted mx-auto mb-3" />
              <p className="text-text-muted">No tienes tickets de soporte</p>
              <Button onClick={() => setShowNewTicket(true)} className="mt-4 bg-brand">
                Crear mi primer ticket
              </Button>
            </div>
          ) : (
            tickets.map(ticket => {
              const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || AlertCircle;
              return (
                <div
                  key={ticket.id}
                  onClick={() => fetchTicketDetail(ticket.id)}
                  className="bg-surface border border-border-subtle rounded-xl p-4 cursor-pointer hover:border-brand/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-text-muted text-sm">#{ticket.id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[ticket.status]?.color}`}>
                          {STATUS_CONFIG[ticket.status]?.label}
                        </span>
                        <span className="text-xs text-text-muted">
                          {CATEGORY_CONFIG[ticket.category]?.emoji} {CATEGORY_CONFIG[ticket.category]?.label}
                        </span>
                      </div>
                      <h3 className="font-medium text-text-main truncate">{ticket.subject}</h3>
                      <p className="text-sm text-text-muted mt-1">
                        {ticket.messages?.length || 0} mensaje(s) • Actualizado {formatDate(ticket.updated_at)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* New Ticket Modal */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="bg-surface border-border-subtle max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-text-main">Nuevo Ticket de Soporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-text-muted mb-1 block">Categoría</label>
              <select
                value={newTicketForm.category}
                onChange={(e) => setNewTicketForm({ ...newTicketForm, category: e.target.value })}
                className="w-full h-10 px-3 rounded-lg bg-app-bg border border-border-subtle text-text-main"
              >
                {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
                  <option key={key} value={key}>{val.emoji} {val.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-text-muted mb-1 block">Asunto</label>
              <Input
                value={newTicketForm.subject}
                onChange={(e) => setNewTicketForm({ ...newTicketForm, subject: e.target.value })}
                placeholder="Describe brevemente tu problema"
                className="bg-app-bg border-border-subtle"
              />
            </div>
            <div>
              <label className="text-sm text-text-muted mb-1 block">Mensaje</label>
              <Textarea
                value={newTicketForm.message}
                onChange={(e) => setNewTicketForm({ ...newTicketForm, message: e.target.value })}
                placeholder="Explica tu problema o pregunta con detalle..."
                className="min-h-[120px] bg-app-bg border-border-subtle"
              />
            </div>
            
            {/* Attachments */}
            <div>
              <label className="text-sm text-text-muted mb-2 block">Adjuntos (opcional)</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleFileSelect(e)}
                className="hidden"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
              />
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((file, idx) => (
                  <div key={file.name || `attachment-${idx}`} className="flex items-center gap-2 px-3 py-1.5 bg-app-bg rounded-lg text-sm">
                    <Paperclip className="w-4 h-4 text-text-muted" />
                    <span className="truncate max-w-[150px]">{file.name}</span>
                    <button onClick={() => removeAttachment(idx)} className="text-red-400 hover:text-red-300">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-1" /> Subir archivo
              </Button>
              <p className="text-xs text-text-muted mt-1">Máx 10MB por archivo. Imágenes, PDF, documentos.</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNewTicket(false)}>Cancelar</Button>
              <Button 
                onClick={handleCreateTicket}
                disabled={submitting}
                className="bg-brand"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Crear Ticket
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default UserSupport;
